import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppGate } from '@/hooks/useAppGate';
import {
  addLocalReminderListeners,
  currentFirstName,
  getReminderArmedAt,
  isLocalNotificationsGranted,
  isLocalRemindersSupported,
  registerReminderActionTypes,
  remindersFiredSince,
  rescheduleFromSnapshot,
} from '@/lib/localReminders';
import { ensureLocalFeedEntry } from '@/lib/notifications/localFeed';
import { loadLocalPreferences } from '@/lib/preferences/snapshot';
import {
  addPushListeners,
  ensureNotificationChannel,
  flushPendingToken,
  registerIfGranted,
} from '@/lib/push';
import { queryKeys } from '@/lib/query';
import { useAuthStore } from '@/stores/authStore';

export function usePushRegistration(): void {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const anonId = useAuthStore((s) => s.anonId);
  const gateReady = useAppGate().status === 'ready';

  const invalidateFeed = useCallback(
    () => void qc.invalidateQueries({ queryKey: queryKeys.notifications.all, refetchType: 'all' }),
    [qc],
  );

  useEffect(
    () => addPushListeners((route) => navigate(route), invalidateFeed),
    [navigate, invalidateFeed],
  );

  useEffect(() => {
    void ensureNotificationChannel();
    void registerReminderActionTypes();
  }, []);

  useEffect(() => {
    // pre-ready, a retained cold-start tap would hit AppGate's /login redirect
    if (!gateReady) return;
    return addLocalReminderListeners(
      (route) => navigate(route),
      (type) =>
        void ensureLocalFeedEntry(type, currentFirstName(), new Date().toISOString()).then(
          invalidateFeed,
        ),
    );
  }, [gateReady, navigate, invalidateFeed]);

  useEffect(() => {
    if (!anonId) return;
    void flushPendingToken();
    void registerIfGranted();
    void rescheduleFromSnapshot();
  }, [anonId]);

  // recover same-session grants (settings round-trip) + reboot; backfill the feed
  // for reminders that fired while backgrounded and were never tapped
  useEffect(() => {
    if (!isLocalRemindersSupported()) return;
    const sync = async () => {
      if (document.visibilityState !== 'visible') return;
      await rescheduleFromSnapshot();
      if (!(await isLocalNotificationsGranted())) return;
      const now = new Date();
      const fired = remindersFiredSince(loadLocalPreferences(), now, await getReminderArmedAt());
      const wrote = await Promise.all(
        fired.map((type) => ensureLocalFeedEntry(type, currentFirstName(), now.toISOString())),
      );
      if (wrote.some(Boolean)) invalidateFeed();
    };
    const onVisible = () => void sync();
    void sync();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [invalidateFeed]);
}
